/**
 * On-screen 영속 로드 진단 (?diag=1 게이트).
 *
 * 목적: iOS WebKit 이 로드 중 메모리 초과로 탭을 크래시-리로드할 때, Windows 에서
 * 콘솔을 볼 수 없으므로 "어느 스테이지까지 도달하고 죽었는지"를 알 방법이 없다.
 * → 각 스테이지 마커를 즉시 localStorage 에 동기 기록. 크래시-리로드 후 다음 런의
 *   오버레이가 "지난 런의 마지막 도달 스테이지"를 보여줘 사망 지점을 특정한다.
 *
 * ?diag=1 이 아니면 전 함수가 완전 무동작 — localStorage 도 DOM 도 오버헤드도 없음.
 * 함수형 API 만; 클래스/레지스트리/mode manager 없음.
 */

const STORAGE_KEY = 'vw-loadDiag';
const OVERLAY_ID = 'load-diag';
const MAX_ERRORS = 8; // 오버레이/스토리지에 남길 최근 에러 수 상한

interface DiagMark {
  stage: string;
  /** 런 시작(첫 diagMark) 기준 상대 ms */
  t: number;
}

interface DiagRun {
  marks: DiagMark[];
  errors: string[];
  info: string;
}

/** ?diag=1 활성 시에만 세팅되는 모듈 상태. 비활성이면 null → 전 함수 no-op. */
let state: {
  run: DiagRun;
  start: number;
  overlay: HTMLElement;
  prevText: string;
} | null = null;

/**
 * diag 모드 초기화. createViewer 최상단에서 1회 호출.
 * ?diag=1 이 아니면 즉시 반환(무동작). 활성이면 지난 런 기록을 읽어 오버레이를
 * 렌더하고, window.onerror / unhandledrejection / webglcontextlost 를 후킹한다.
 */
export function initLoadDiag(params: URLSearchParams): void {
  if (params.get('diag') !== '1') return;
  if (state) return; // 중복 호출 방어

  const previous = readPrevRun();
  const overlay = createOverlay();
  document.body.appendChild(overlay);

  state = {
    run: { marks: [], errors: [], info: '' },
    start: performance.now(),
    overlay,
    prevText: formatPrevRun(previous),
  };

  wireErrorHooks();
  render();
}

/**
 * 로드 스테이지 마커. 호출 즉시 상대 타임스탬프와 함께 localStorage 에 동기 기록
 * (크래시 시에도 남도록) 하고 오버레이를 라이브 갱신한다. diag 비활성이면 no-op.
 */
export function diagMark(stage: string): void {
  if (!state) return;
  state.run.marks.push({ stage, t: Math.round(performance.now() - state.start) });
  persist();
  render();
}

/**
 * 참고 정보 1줄 (devicePixelRatio / 화면 크기 / renderer.info 텍스처·지오메트리 수).
 * first-frame 이후 renderer.info 가 채워진 시점에 호출. diag 비활성이면 no-op.
 */
export function diagInfo(renderer?: {
  info: { memory: { textures: number; geometries: number } };
}): void {
  if (!state) return;
  const dpr = window.devicePixelRatio;
  const w = window.innerWidth;
  const h = window.innerHeight;
  let mem = '';
  if (renderer) {
    const m = renderer.info.memory;
    mem = ` tex=${m.textures} geo=${m.geometries}`;
  }
  state.run.info = `dpr=${dpr} ${w}x${h}${mem}`;
  persist();
  render();
}

/** 지난 런 기록 읽기. 파싱 실패/부재는 null. */
function readPrevRun(): DiagRun | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as DiagRun;
  } catch {
    return null;
  }
}

/** 현재 런을 localStorage 에 동기 기록. 크래시 직전 마지막 마커까지 남기는 게 핵심. */
function persist(): void {
  if (!state) return;
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state.run));
  } catch {
    // storage full/차단 — 진단 목적이라 조용히 무시
  }
}

function recordError(msg: string): void {
  if (!state) return;
  const t = Math.round(performance.now() - state.start);
  state.run.errors.push(`+${t}ms ${msg}`);
  if (state.run.errors.length > MAX_ERRORS) {
    state.run.errors.splice(0, state.run.errors.length - MAX_ERRORS);
  }
  persist();
  render();
}

function wireErrorHooks(): void {
  window.addEventListener('error', (e: ErrorEvent) => {
    recordError(`onerror: ${e.message}`);
  });
  window.addEventListener('unhandledrejection', (e: PromiseRejectionEvent) => {
    recordError(`unhandled: ${String(e.reason)}`);
  });
  // WebGL context lost — iOS 가 메모리 압박으로 컨텍스트를 뺏을 때 발생.
  // 이 훅은 canvas 생성 전(setupRenderer 전)에 걸리므로, document 캡처 단계로 청취
  // (webglcontextlost 는 버블 안 하지만 캡처 단계 조상 리스너는 발화). canvas 생성
  // 타이밍과 무관하게 잡힌다.
  document.addEventListener('webglcontextlost', () => {
    recordError('webglcontextlost');
  }, true);
}

function createOverlay(): HTMLElement {
  const el = document.createElement('div');
  el.id = OVERLAY_ID;
  el.style.cssText = [
    'position:fixed',
    'top:0',
    'left:0',
    'z-index:9999',
    'max-width:60vw',
    'padding:6px 8px',
    'background:rgba(0,0,0,0.72)',
    'color:#8f8',
    'font:11px/1.45 ui-monospace,monospace',
    'white-space:pre',
    'pointer-events:none',
    'border-bottom-right-radius:4px',
  ].join(';');
  return el;
}

function formatPrevRun(prev: DiagRun | null): string {
  if (!prev || !prev.marks.length) {
    return 'PREV: (none)';
  }
  const last = prev.marks[prev.marks.length - 1]!;
  const lines = [`PREV last: ${last.stage} @+${last.t}ms`];
  for (const m of prev.marks) lines.push(`  ${pad(m.t)} ${m.stage}`);
  if (prev.info) lines.push(`  info: ${prev.info}`);
  for (const err of prev.errors) lines.push(`  ! ${err}`);
  return lines.join('\n');
}

function formatCurrentRun(): string {
  if (!state) return '';
  const lines = ['CUR:'];
  for (const m of state.run.marks) lines.push(`  ${pad(m.t)} ${m.stage}`);
  if (state.run.info) lines.push(`  info: ${state.run.info}`);
  for (const err of state.run.errors) lines.push(`  ! ${err}`);
  return lines.join('\n');
}

function render(): void {
  if (!state) return;
  state.overlay.textContent = `${state.prevText}\n\n${formatCurrentRun()}`;
}

/** 상대 ms 를 우측정렬 6자리로 (라인 정렬용) */
function pad(t: number): string {
  return `+${t}`.padStart(6, ' ');
}
